import { AnnualReportNavigationDirection, AnnualReportProps } from '../AnnualReport';
import { Button, Input } from '../../../components';
import { Card, CardBody, CardFooter, Col, Form, FormGroup, Label, Row } from 'reactstrap';
import React, { useEffect, useState } from 'react';

import { ProgramDistanceEducation } from '../../../common/Types';
import { programService } from '../../../services/ProgramService';
import { toast } from 'react-toastify';
import { useForm, Controller } from 'react-hook-form';

const DistanceEducation = (props: AnnualReportProps) => {
    const {
        handleSubmit,
        control,
        watch,
        setValue,
        getValues,
        reset,
        formState: { errors },
    } = useForm();
    const [formSubmitting, setFormSubmitting] = useState<boolean>(false);
    const [distanceEd, setDistanceEd] = useState<ProgramDistanceEducation>();
    const [submitDirection, setSubmitDirection] = useState<AnnualReportNavigationDirection>(
        AnnualReportNavigationDirection.Forward,
    );

    const onSubmit = (formData) => {
        setFormSubmitting(true);
        const toastId = toast.info('Saving Distance Education Detail...');

        const payload = new ProgramDistanceEducation({
            ...formData,
            distanceId: formData.distanceId ?? distanceEd?.distanceId ?? 0,
            programId: formData.programId ?? props.program.programId,
            activeYear: formData.activeYear ?? +props.reportYear,
        });

        programService
            .saveProgramDistanceEducation(props.program.programId, payload)
            .then((result) => {
                // Update form with server response (especially distanceId for new records)
                if (result) {
                    reset(result);
                    setDistanceEd(result);
                }
                toast.update(toastId, {
                    type: 'success',
                    render: 'Details Saved.',
                });

                if (submitDirection === AnnualReportNavigationDirection.Forward) {
                    props.onSavedSuccessfully(AnnualReportNavigationDirection.Forward);
                }
            })
            .catch(() => {
                toast.update(toastId, {
                    type: 'error',
                    render: 'Failed to save Details.',
                });
            })
            .finally(() => {
                setFormSubmitting(false);
            });
    };

    const submitWithoutValidation = () => {
        setFormSubmitting(true);
        const toastId = toast.info('Saving Distance Education Detail...');

        const formValues = getValues();
        const payload = new ProgramDistanceEducation({
            ...formValues,
            distanceId: formValues.distanceId ?? distanceEd?.distanceId ?? 0,
            programId: formValues.programId ?? props.program.programId,
            activeYear: formValues.activeYear ?? +props.reportYear,
        });

        programService
            .saveProgramDistanceEducation(props.program.programId, payload)
            .then((result) => {
                toast.update(toastId, {
                    type: 'success',
                    render: 'Details Saved.',
                });

                reset(result);
                setDistanceEd(result);
            })
            .catch(() => {
                toast.update(toastId, {
                    type: 'error',
                    render: 'Failed to save Details.',
                });
            })
            .finally(() => {
                setFormSubmitting(false);
            });
    };

    useEffect(() => {
        const getResources = () => {
            if (props.program.programId && +props.reportYear) {
                programService
                    .getProgramDistanceEducation(props.program.programId, props.reportYear ?? new Date().getFullYear())
                    .then((response) => {
                        setDistanceEd(response);
                        reset(response);

                        if (props.hasTabAlreadyBeenSubmitted && !response?.distanceId) {
                            setValue('distanceLearning', false);
                        }
                    })
                    .catch(() => {
                        toast.error('Unable to get settings.');
                    });
            }
        };

        getResources();
    }, [props.program.programId, props.reportYear, setDistanceEd, props.hasTabAlreadyBeenSubmitted, setValue, reset]);

    const watchDistance: boolean = watch('distanceLearning');

    return (
        <Card>
            <Form noValidate onSubmit={handleSubmit(onSubmit, (validationErrors) => {
                const fieldNames = Object.keys(validationErrors);
                toast.error(`Please fix the following fields: ${fieldNames.join(', ')}`);
            })}>
                <Controller
                    name="distanceId"
                    control={control}
                    render={({ field }) => (
                        <input type="hidden" {...field} value={field.value ?? (distanceEd?.distanceId || 0)} />
                    )}
                />
                <Controller
                    name="programId"
                    control={control}
                    render={({ field }) => (
                        <input type="hidden" {...field} value={field.value ?? props.program.programId} />
                    )}
                />
                <Controller
                    name="activeYear"
                    control={control}
                    render={({ field }) => (
                        <input type="hidden" {...field} value={field.value ?? +props.reportYear} />
                    )}
                />
                <CardBody>
                    <h3 className="card-title">Distance Education</h3>
                    <FormGroup>
                        <div>
                            <Label>Is any portion of the program offered through distance learning?</Label>
                        </div>
                        <Controller
                            name="distanceLearning"
                            control={control}
                            rules={{ validate: (value) => (value != null && value !== '') || 'Please select Yes or No' }}
                            render={({ field }) => (
                                <div className={'d-flex'}>
                                    <div className={'custom-control custom-radio me-3'}>
                                        <input
                                            type="radio"
                                            id={'distanceLearningYes'}
                                            className="custom-control-input"
                                            name={field.name}
                                            checked={field.value === 'true' || field.value === true}
                                            value="true"
                                            onChange={() => field.onChange('true')}
                                        />
                                        <label className="custom-control-label" htmlFor={'distanceLearningYes'}>{`Yes`}</label>
                                    </div>
                                    <div className={'custom-control custom-radio'}>
                                        <input
                                            type="radio"
                                            id={'distanceLearningNo'}
                                            className="custom-control-input"
                                            name={field.name}
                                            checked={field.value === 'false' || field.value === false}
                                            value="false"
                                            onChange={() => field.onChange('false')}
                                        />
                                        <label className="custom-control-label" htmlFor={'distanceLearningNo'}>{`No`}</label>
                                    </div>
                                </div>
                            )}
                        />
                        <div>
                            {errors?.distanceLearning?.message && (
                                <Label className={'text-danger'}>{errors.distanceLearning.message as string}</Label>
                            )}
                        </div>
                    </FormGroup>
                    {watchDistance?.toString() === 'true' && (
                        <>
                            <FormGroup>
                                <Label for="percentageOfTheProgram">
                                    Percentage of the program delivered by distance
                                </Label>
                                <Controller
                                    name="percentageOfTheProgram"
                                    control={control}
                                    render={({ field }) => <Input type="number" {...field} value={field.value ?? ''} />}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>
                                    List of courses that are totally web based (ie no face-to-face instruction)
                                </Label>
                                <Controller
                                    name="webBasedPrograms"
                                    control={control}
                                    render={({ field }) => (
                                        <Input type="textarea" {...field} value={field.value ?? ''} />
                                    )}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>
                                    List of courses that are interactive video conferencing to remote locations
                                </Label>
                                <Controller
                                    name="remoteLocations"
                                    control={control}
                                    render={({ field }) => (
                                        <Input type="textarea" {...field} value={field.value ?? ''} />
                                    )}
                                />
                            </FormGroup>
                            <Row>
                                <Col>
                                    <FormGroup>
                                        <Label>Description of Distance Mode</Label>
                                        <FormGroup>
                                            <Controller
                                                name="distanceMode1"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input type="text" {...field} value={field.value ?? ''} />
                                                )}
                                            />
                                        </FormGroup>
                                        <FormGroup>
                                            <Controller
                                                name="distanceMode2"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input type="text" {...field} value={field.value ?? ''} />
                                                )}
                                            />
                                        </FormGroup>
                                        <FormGroup>
                                            <Controller
                                                name="distanceMode3"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input type="text" {...field} value={field.value ?? ''} />
                                                )}
                                            />
                                        </FormGroup>
                                        <FormGroup>
                                            <Controller
                                                name="distanceMode4"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input type="text" {...field} value={field.value ?? ''} />
                                                )}
                                            />
                                        </FormGroup>
                                    </FormGroup>
                                </Col>
                                <Col>
                                    <FormGroup>
                                        <Label>List of courses delivered in that mode</Label>
                                        <FormGroup>
                                            <Controller
                                                name="listOfCourses1"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input type="text" {...field} value={field.value ?? ''} />
                                                )}
                                            />
                                        </FormGroup>
                                        <FormGroup>
                                            <Controller
                                                name="listOfCourses2"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input type="text" {...field} value={field.value ?? ''} />
                                                )}
                                            />
                                        </FormGroup>
                                        <FormGroup>
                                            <Controller
                                                name="listOfCourses3"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input type="text" {...field} value={field.value ?? ''} />
                                                )}
                                            />
                                        </FormGroup>
                                        <FormGroup>
                                            <Controller
                                                name="listOfCourses4"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input type="text" {...field} value={field.value ?? ''} />
                                                )}
                                            />
                                        </FormGroup>
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Controller
                                name="createdBy"
                                control={control}
                                render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                            />
                            <Controller
                                name="dateCreated"
                                control={control}
                                render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                            />
                            <Controller
                                name="lastEdited"
                                control={control}
                                render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                            />
                            <Controller
                                name="editedBy"
                                control={control}
                                render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                            />
                        </>
                    )}
                </CardBody>
                {props.canEdit && (
                    <CardFooter className={'d-flex justify-content-between'}>
                        <Button
                            type={'button'}
                            color={'warning'}
                            onClick={() => props.onNavigate(AnnualReportNavigationDirection.Back)}
                        >
                            <i className={'mdi mdi-arrow-left'} />
                            <span className={'mr-1'}>{`Previous`}</span>
                        </Button>

                        <Button
                            disabled={formSubmitting}
                            type={'button'}
                            color={'primary'}
                            onClick={() => submitWithoutValidation()}
                        >
                            <i className={'mdi mdi-content-save'} />
                            <span className={'ml-1'}>{`Save`}</span>
                        </Button>

                        <Button
                            disabled={formSubmitting}
                            type={'submit'}
                            color={'info'}
                            onClick={() => setSubmitDirection(AnnualReportNavigationDirection.Forward)}
                        >
                            <span className={'mr-1'}>{`Save & Continue`}</span>
                            <i className={'mdi mdi-arrow-right'} />
                        </Button>
                    </CardFooter>
                )}
            </Form>
        </Card>
    );
};

export default DistanceEducation;
